import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import RichText from '../components/RichText';
import { Navigate, useParams } from 'react-router-dom';
import useMeetingRoom from '../hooks/useMeetingRoom';
import useMeetingSession from '../hooks/useMeetingSession';
import MeetingPreJoin from '../components/meeting/MeetingPreJoin';
import MeetingRoomView from '../components/meeting/MeetingRoomView';
import { dayText, rangeText, timeText } from '../components/meeting/meetingHelpers';
import { guestMeetingApi, guestPublic } from '../utils/meetingApi';
import '../styles/meetings.css';

/**
 * /meet/<token> and /meet/<abc-defg-hij> - the door for people with no account.
 *
 * With an invitation link the guest is already on the list: they can answer
 * Yes / Maybe / No, and join. With only the meeting code they ask to come in,
 * wait for a host to let them in, and then join the same way.
 *
 * Deliberately outside the login and the app's sidebar (App.js): the first ten
 * seconds are everything. It loads fast, says what it is, and asks for the
 * camera and microphone in plain words.
 */

const CODE = /^[a-z]{3}-[a-z]{4}-[a-z]{3}$/;

const hasPortalSession = () => {
    try { return Boolean(localStorage.getItem('token')); } catch { return false; }
};
const RSVP_WORDS = { accepted: "You're attending", tentative: 'You might attend', declined: "You can't attend" };

const Shell = ({ children }) => (
    <div className="mt-guest-page">
        <header className="mt-guest-head"><img src="/GTS.png" alt="" /><span>GTS Portal</span></header>
        <main>{children}</main>
    </div>
);

const Card = ({ title, children }) => (
    <div className="mt-center-card">
        <h2>{title}</h2>
        {children}
    </div>
);

const errorText = (err, fallback) => err.response?.data?.message || fallback;

/** The meeting's details, in the guest's own timezone. */
const Details = ({ meeting }) => (
    <div className="mt-guest-details">
        <h2>{meeting.title}</h2>
        <p>
            {dayText(meeting.startsAt, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            <br />
            {rangeText(meeting)} <span className="mt-subtle">(your local time)</span>
        </p>
        {meeting.organizer && <p className="mt-subtle">Organised by {meeting.organizer}</p>}
        {meeting.location && !meeting.hasRoom && <p>Where: {meeting.location}</p>}
        {meeting.description && <RichText text={meeting.description} className="mt-description" />}
    </div>
);

const GuestMeeting = () => {
    const { token: param } = useParams();
    const isCode = CODE.test(String(param || '').toLowerCase());
    const code = String(param || '').toLowerCase();

    // The token that identifies this guest: the link itself, or - for somebody
    // who came with only a code - the one they are given when let in.
    const [token, setToken] = useState(isCode ? null : param);
    const [info, setInfo] = useState(null);
    const [codeInfo, setCodeInfo] = useState(null);
    const [problem, setProblem] = useState('');
    const [rsvp, setRsvp] = useState('');
    const [pendingRsvp, setPendingRsvp] = useState(() => {
        const wanted = new URLSearchParams(window.location.search).get('rsvp');
        return ['accepted', 'tentative', 'declined'].includes(wanted) ? wanted : '';
    });
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [waitToken, setWaitToken] = useState(null);
    const [waitState, setWaitState] = useState('');
    const [joining, setJoining] = useState(false);
    const [waitingForHost, setWaitingForHost] = useState(false);
    // Guests are never hosts.
    const canHost = false;

    const room = useMeetingRoom();
    const retry = useRef(null);
    const joinedWith = useRef(isCode ? 'code' : 'link');

    // ---- the details ----
    useEffect(() => {
        let alive = true;
        if (isCode) {
            guestPublic.byCode(code)
                .then((res) => { if (alive) setCodeInfo(res); })
                .catch(() => { if (alive) setProblem('We could not find that meeting. Check the code and try again.'); });
        }
        return () => { alive = false; };
    }, [isCode, code]);

    useEffect(() => {
        if (!token) return undefined;
        let alive = true;
        guestPublic.info(token)
            .then((res) => {
                if (!alive) return;
                setInfo(res);
                setRsvp(res.guest.rsvp);
                setName((current) => current || res.guest.name || '');
            })
            .catch(() => { if (alive) setProblem('This link is not valid or has expired.'); });
        return () => { alive = false; };
    }, [token]);

    // ---- waiting to be let in (code visitors) ----
    useEffect(() => {
        if (!waitToken) return undefined;
        let alive = true;
        let timer = null;
        const tick = async () => {
            try {
                const res = await guestPublic.waitStatus(waitToken);
                if (!alive) return;
                if (res.status === 'admitted' && res.token) { setWaitToken(null); setToken(res.token); return; }
                if (res.status === 'denied') { setWaitState('denied'); setWaitToken(null); return; }
            } catch { /* keep trying */ }
            if (alive) timer = setTimeout(tick, 3000);
        };
        tick();
        return () => { alive = false; clearTimeout(timer); };
    }, [waitToken]);

    useEffect(() => () => clearTimeout(retry.current), []);

    const askToJoin = async (e) => {
        e.preventDefault();
        setProblem('');
        try {
            const res = await guestPublic.requestEntry(code, { name, email });
            if (res.status === 'admitted') setToken(res.token);
            else { setWaitState('waiting'); setWaitToken(res.waitToken); }
        } catch (err) {
            setProblem(errorText(err, 'Could not ask to join. Please try again.'));
        }
    };

    const answer = async (value) => {
        try {
            await guestPublic.rsvp(token, value);
            setRsvp(value);
            setPendingRsvp('');
        } catch (err) {
            setProblem(errorText(err, 'Could not save your answer.'));
        }
    };

    const join = useCallback(async (choices) => {
        clearTimeout(retry.current);
        setJoining(true);
        setProblem('');
        try {
            const res = await guestPublic.join(token, { name, joinMethod: joinedWith.current });
            setWaitingForHost(false);
            await room.connect({ ...res, ...choices });
        } catch (err) {
            const data = err.response?.data;
            if (data?.code === 'WAITING_FOR_HOST') {
                // Keep trying quietly until somebody from the company arrives.
                setWaitingForHost(true);
                retry.current = setTimeout(() => join(choices), 5000);
            } else if (data?.code === 'TOO_EARLY' && data.opensAt) {
                setProblem(`The room opens at ${timeText(data.opensAt)} on ${dayText(data.opensAt, { day: 'numeric', month: 'short' })}.`);
            } else {
                setProblem(data?.message || 'Could not join the meeting. Please try again.');
            }
        } finally {
            setJoining(false);
        }
    }, [token, name, room]);

    const client = useMemo(() => (token ? guestMeetingApi(token) : null), [token]);
    const session = useMeetingSession({ client });
    const live = room.status === 'connected' || room.status === 'reconnecting';

    // ---- what to show ----
    // Somebody signed in to the portal who opens the shared link joins as themselves - with
    // their own name and account, not as an anonymous guest - and waits in the lobby if they
    // were not invited.
    if (isCode && hasPortalSession()) return <Navigate to={`/meetings/join/${code}`} replace />;

    if (live && info) {
        return (
            <Shell>
                <MeetingRoomView
                    title={info.meeting.title}
                    room={room}
                    session={session}
                    canHost={canHost}
                    onLeave={() => room.disconnect('You left the meeting.')}
                />
            </Shell>
        );
    }

    if (room.status === 'ended') {
        return (
            <Shell>
                <Card title={room.message || 'You left the meeting.'}>
                    <button type="button" className="mt-btn primary" onClick={() => window.location.reload()}>Rejoin</button>
                </Card>
            </Shell>
        );
    }

    if (problem && !info && !codeInfo) {
        return <Shell><Card title={problem} /></Shell>;
    }

    // Somebody with only the code: who are you, then wait for a host.
    if (isCode && !token) {
        if (waitState === 'denied') {
            return <Shell><Card title="The host did not let you in."><p className="mt-subtle">If you think this is a mistake, ask the organizer to invite you.</p></Card></Shell>;
        }
        if (waitToken) {
            return (
                <Shell>
                    <Card title="Waiting for the host to let you in">
                        <p className="mt-subtle">{codeInfo?.title}</p>
                        <p className="mt-subtle">You can keep this page open. It will move on by itself.</p>
                    </Card>
                </Shell>
            );
        }
        if (!codeInfo) return <Shell><Card title="Loading…" /></Shell>;
        return (
            <Shell>
                <form className="mt-center-card" onSubmit={askToJoin}>
                    <h2>{codeInfo.title}</h2>
                    {codeInfo.organizer && <p className="mt-subtle">Organised by {codeInfo.organizer}</p>}
                    <label className="mt-field">
                        <span>Your name</span>
                        <input type="text" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} autoFocus />
                    </label>
                    <label className="mt-field">
                        <span>Your email (optional)</span>
                        <input type="text" value={email} onChange={(e) => setEmail(e.target.value)} />
                    </label>
                    {problem && <p className="mt-notice">{problem}</p>}
                    <button type="submit" className="mt-btn primary" disabled={!name.trim()}>
                        {codeInfo.lobby ? 'Ask to join' : 'Join'}
                    </button>
                </form>
            </Shell>
        );
    }

    if (!info) return <Shell><Card title="Loading…" /></Shell>;

    const { meeting } = info;
    const joinable = meeting.hasRoom && ['scheduled', 'live'].includes(meeting.status);
    const opensAt = info.join?.opensAt && !info.join.open ? new Date(info.join.opensAt) : null;

    return (
        <Shell>
            <div className="mt-guest-layout">
                <Details meeting={meeting} />

                {meeting.status === 'cancelled' && <p className="mt-notice">This {meeting.kind} has been cancelled.</p>}

                {meeting.status !== 'cancelled' && (
                    <div className="mt-rsvp">
                        {pendingRsvp && pendingRsvp !== rsvp ? (
                            <>
                                <span>Confirm your answer: <strong>{RSVP_WORDS[pendingRsvp]}</strong></span>
                                <div className="mt-rsvp-buttons">
                                    <button type="button" className="mt-btn small primary" onClick={() => answer(pendingRsvp)}>Confirm</button>
                                    <button type="button" className="mt-btn small ghost" onClick={() => setPendingRsvp('')}>Change</button>
                                </div>
                            </>
                        ) : (
                            <>
                                <span>Will you attend?{rsvp && rsvp !== 'pending' ? <> <strong>{RSVP_WORDS[rsvp]}</strong></> : null}</span>
                                <div className="mt-rsvp-buttons">
                                    {[['accepted', 'Yes'], ['tentative', 'Maybe'], ['declined', 'No']].map(([value, label]) => (
                                        <button
                                            key={value}
                                            type="button"
                                            className={`mt-btn small ${rsvp === value ? 'primary' : 'ghost'}`}
                                            onClick={() => answer(value)}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                )}

                {problem && <p className="mt-notice">{problem}</p>}

                {joinable && (
                    <>
                        {waitingForHost && (
                            <p className="mt-notice">Waiting for the host to start the meeting. You will be let in automatically.</p>
                        )}
                        <MeetingPreJoin
                            meeting={{ title: meeting.title, organizer: meeting.organizer }}
                            joining={joining || room.status === 'connecting'}
                            onJoin={join}
                            askName
                            name={name}
                            onNameChange={setName}
                            note={opensAt ? `The room opens at ${timeText(opensAt)} on ${dayText(opensAt, { day: 'numeric', month: 'short' })}.` : ''}
                        />
                    </>
                )}

                {!meeting.hasRoom && meeting.status !== 'cancelled' && (
                    <p className="mt-subtle">This is an in-person event, so there is nothing to join online.</p>
                )}
            </div>
        </Shell>
    );
};

export default GuestMeeting;
